import { supabase } from './supabase';
import { slugify } from './slug';
import { taskService } from './taskService';

export type BoardBackground = {
  type: 'gradient' | 'color' | 'image';
  value: string;
  overlay?: string;
};

export interface TaskBoard {
  id: string;
  workspace_id: string;
  slug: string;
  title: string;
  description: string;
  background: BoardBackground;
  hide_banner?: boolean;
  enable_upvotes?: boolean;
  enableUpvotes?: boolean;
  columns: any[];
  tasks: any[];
  active_tasks_count?: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

const workspaceBoardsCache = new Map<string, TaskBoard[]>();
const boardDetailsCache = new Map<string, TaskBoard>();
const prefetchPromises = new Map<string, Promise<any>>();

export const boardService = {
  clearCache() {
    workspaceBoardsCache.clear();
    boardDetailsCache.clear();
    prefetchPromises.clear();
  },

  async getBoardsByWorkspace(workspaceId: string): Promise<TaskBoard[]> {
    if (workspaceBoardsCache.has(workspaceId)) {
      return workspaceBoardsCache.get(workspaceId)!;
    }

    const { data, error } = await supabase
      .from('task_boards')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    workspaceBoardsCache.set(workspaceId, data || []);
    return data || [];
  },

  async getBoardById(boardId: string): Promise<TaskBoard | null> {
    if (boardDetailsCache.has(boardId)) {
      return boardDetailsCache.get(boardId)!;
    }

    const { data, error } = await supabase
      .from('task_boards')
      .select('*')
      .eq('id', boardId)
      .maybeSingle();

    if (error) throw error;
    if (data) boardDetailsCache.set(boardId, data);
    return data;
  },

  async getBoardBySlug(workspaceId: string, slug: string): Promise<TaskBoard | null> {
    const cacheKey = `${workspaceId}:${slug}`;
    if (boardDetailsCache.has(cacheKey)) {
      return boardDetailsCache.get(cacheKey)!;
    }

    const { data, error } = await supabase
      .from('task_boards')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('slug', slug)
      .maybeSingle();

    if (error) throw error;

    if (data) {
      boardDetailsCache.set(cacheKey, data);
    }

    return data;
  },

  async prefetchBoardData(workspaceId: string, slug: string) {
    const cacheKey = `${workspaceId}:${slug}`;
    if (boardDetailsCache.has(cacheKey) || prefetchPromises.has(cacheKey)) return;

    const promise = (async () => {
      try {
        const board = await this.getBoardBySlug(workspaceId, slug);
        if (board) {
          // Trigger prefetch for tasks and columns
          await taskService.prefetchBoardContent(board.id);
          return board;
        }
      } catch (error) {
        console.error('Prefetch error:', error);
      } finally {
        prefetchPromises.delete(cacheKey);
      }
    })();

    prefetchPromises.set(cacheKey, promise);
    return promise;
  },

  async createBoard(
    workspaceId: string,
    userId: string,
    title: string,
    description: string,
    background: BoardBackground
  ): Promise<TaskBoard> {
    const slug = await this.generateUniqueSlug(workspaceId, title);

    const { data, error } = await supabase
      .from('task_boards')
      .insert({
        workspace_id: workspaceId,
        slug,
        title,
        description,
        background,
        hide_banner: true,
        columns: [],
        tasks: [],
        created_by: userId,
      })
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache
    workspaceBoardsCache.delete(workspaceId);

    // Create default columns
    const defaultColumns = [
      { title: 'Open', position: 0 },
      { title: 'To Do', position: 1 },
      { title: 'In Progress', position: 2 },
      { title: 'Done', position: 3 },
    ];

    const columnResults = await Promise.all(
      defaultColumns.map(col =>
        supabase.from('task_columns').insert({
          board_id: data.id,
          title: col.title,
          position: col.position,
        })
      )
    );

    // Check for errors in column creation
    const columnErrors = columnResults.filter(r => r.error);
    if (columnErrors.length > 0) {
      console.error('Error creating default columns:', columnErrors);
      throw new Error(`Failed to create default columns: ${columnErrors[0].error?.message}`);
    }

    return data;
  },

  async updateBoard(
    boardId: string,
    updates: {
      title?: string;
      description?: string;
      background?: BoardBackground;
      slug?: string;
      hideBanner?: boolean;
      enableUpvotes?: boolean;
      enable_upvotes?: boolean;
      columns?: any[];
      tasks?: any[];
    }
  ): Promise<TaskBoard> {
    // Map hideBanner and enableUpvotes for database
    const dbUpdates: any = { ...updates };
    if ('hideBanner' in updates) {
      dbUpdates.hide_banner = updates.hideBanner;
      delete dbUpdates.hideBanner;
    }
    if ('enableUpvotes' in updates) {
      dbUpdates.enable_upvotes = updates.enableUpvotes;
      delete dbUpdates.enableUpvotes;
    }

    let { data, error } = await supabase
      .from('task_boards')
      .update({ ...dbUpdates, updated_at: new Date().toISOString() })
      .eq('id', boardId)
      .select()
      .single();

    // If enable_upvotes column doesn't exist yet on remote database, gracefully fallback
    if (error && (error.message?.includes('enable_upvotes') || error.code === '42703')) {
      const upvotesSetting = dbUpdates.enable_upvotes;
      delete dbUpdates.enable_upvotes;
      const retry = await supabase
        .from('task_boards')
        .update({ ...dbUpdates, updated_at: new Date().toISOString() })
        .eq('id', boardId)
        .select()
        .single();
      data = retry.data;
      error = retry.error;
      if (data && upvotesSetting !== undefined) {
        data.enable_upvotes = upvotesSetting;
      }
    }

    if (error) throw error;

    // Invalidate cache
    workspaceBoardsCache.delete(data.workspace_id);
    const cacheKey = `${data.workspace_id}:${data.slug}`;
    boardDetailsCache.delete(cacheKey);

    return data;
  },

  async deleteBoard(boardId: string): Promise<void> {
    const { error } = await supabase
      .from('task_boards')
      .delete()
      .eq('id', boardId);

    if (error) throw error;

    // Invalidate cache
    workspaceBoardsCache.clear();
    boardDetailsCache.clear();
  },

  async generateUniqueSlug(workspaceId: string, title: string): Promise<string> {
    const baseSlug = slugify(title);

    const { data, error } = await supabase
      .from('task_boards')
      .select('slug')
      .eq('workspace_id', workspaceId)
      .eq('slug', baseSlug)
      .maybeSingle();

    if (error) throw error;

    if (!data) {
      return baseSlug;
    }

    let counter = 1;
    let candidateSlug = `${baseSlug}-${counter}`;

    while (true) {
      const { data: existingBoard } = await supabase
        .from('task_boards')
        .select('slug')
        .eq('workspace_id', workspaceId)
        .eq('slug', candidateSlug)
        .maybeSingle();

      if (!existingBoard) {
        return candidateSlug;
      }

      counter++;
      candidateSlug = `${baseSlug}-${counter}`;
    }
  },
};
